/** Native subscription probe UI states (blocked / inconclusive / not registered). */

export const PROBE_STATE_LABELS = {
  blocked: 'Blocked',
  inconclusive: 'Inconclusive',
  not_registered: 'Not registered',
};

export const PROBE_STATE_STYLES = {
  blocked: 'border-amber-500/30 bg-amber-950/25 text-amber-200/90',
  inconclusive: 'border-slate-500/30 bg-slate-900/50 text-slate-300/90',
  not_registered: 'border-slate-600/25 bg-slate-950/40 text-slate-400/90',
};

/** Probe key covered when related ecosystem platforms are already registered. */
export const PROBE_COVERED_BY_KEYS = {
  google: ['google', 'youtube', 'google_one', 'gemini', 'google_maps', 'gaia'],
  amazon: ['amazon', 'primevideo'],
  apple: ['apple', 'apple_music', 'icloud'],
  microsoft: ['microsoft', 'office365', 'skype', 'outlook'],
};

/**
 * @param {object} row SSE row with ui_bucket native_probe_status
 * @returns {string|null}
 */
export function probeStateFromRow(row) {
  const state = String(row?.probe_state || row?.status_label || '').toLowerCase();
  if (state in PROBE_STATE_LABELS) return state;
  return null;
}

/**
 * @param {object[]} rows
 * @returns {object[]}
 */
export function collectNativeProbeStatuses(rows) {
  const byKey = new Map();
  (rows || []).forEach((row) => {
    if (String(row?.ui_bucket || '') !== 'native_probe_status') return;
    const state = probeStateFromRow(row);
    if (!state) return;
    const key = String(row?.service_key || row?.service_name || row?.name || '').toLowerCase();
    if (!key) return;
    byKey.set(key, { ...row, probe_state: state });
  });
  return [...byKey.values()].sort((a, b) =>
    String(a?.service_name || a?.name || '').localeCompare(
      String(b?.service_name || b?.name || ''),
      undefined,
      { sensitivity: 'base' }
    )
  );
}

/**
 * Drop probe badges when the same platform (or ecosystem) was confirmed registered.
 * Also drop inconclusive noise when we already have a strong registered footprint.
 *
 * @param {object[]} probeRows
 * @param {object[]} registeredServices
 * @returns {object[]}
 */
export function filterProbeStatusesAgainstRegistered(probeRows, registeredServices) {
  const registeredKeys = new Set(
    (registeredServices || [])
      .map((svc) => String(svc?.service_key || svc?.service_name || svc?.name || '').toLowerCase())
      .filter(Boolean)
  );
  const families = new Set(
    (registeredServices || [])
      .map((svc) => String(svc?.account_family || '').toLowerCase())
      .filter(Boolean)
  );

  return (probeRows || []).filter((row) => {
    const key = String(row?.service_key || row?.service_name || row?.name || '').toLowerCase();
    if (!key) return false;
    if (registeredKeys.has(key)) return false;
    const covers = PROBE_COVERED_BY_KEYS[key];
    if (covers && covers.some((k) => registeredKeys.has(k))) return false;
    const family = String(row?.account_family || '').toLowerCase();
    if (family && families.has(family)) return false;
    // Google / YouTube label without family: suppress when any Google product registered
    if (
      (key.includes('google') || key.includes('youtube')) &&
      (families.has('google') ||
        [...registeredKeys].some((k) => k.includes('google') || k === 'youtube' || k === 'gemini'))
    ) {
      return false;
    }
    return true;
  });
}

/**
 * @param {string} state
 * @returns {string}
 */
export function probeStateLabel(state) {
  return PROBE_STATE_LABELS[String(state || '').toLowerCase()] || 'Inconclusive';
}

/**
 * @param {object} row
 * @returns {string}
 */
export function probeStatusTitle(row) {
  const name = String(row?.service_name || row?.name || 'Service').trim();
  const label = probeStateLabel(row?.probe_state);
  const detail = String(row?.probe_detail || '').replace(/_/g, ' ').trim();
  const method = String(row?.probe_method || '').trim();
  const parts = [label];
  if (detail) parts.push(detail);
  if (method) parts.push(`via ${method}`);
  return `${name} — ${parts.join(' · ')}`;
}
